import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
import { onForward } from './constants';
import * as Yup from 'yup';
import Loader from '@Components/Loader';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import SbTextField from '@Components/Textfield';

// ------- VALIDATION SCHEMA -------
const trackonInformationSchema = Yup.object().shape({
  type: Yup.string().required('Type is required'),
  accountNo: Yup.mixed()
    .test('is-valid', 'Account is required', (value: any) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return true;
      return !!value;
    })
    .required('Account is required'),
  ServiceType: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Service Type is required'),
  TypeOfService: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Type of Service is required'),
  DocType: Yup.string()
    .transform(value => (Array.isArray(value) ? '' : value))
    .required('Doc Type is required'),
});

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showTrackonDetails: boolean;
  btnText: string;
}

const UserTrackon = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showTrackonDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const { shipmentById, userTrackonDetails: userTrackonSettings } = useSelector(
    (state: RootStateType) => state.shipment,
  );
  const { trackonDetails } = useSelector((state: RootStateType) => state.adminShipment);

  // ------------ FORMIK ------------
  const { values, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...initialStates,
      invoiceNumber: invoiceNo,
      accountNo: userTrackonSettings?.accountNo?.length
        ? userTrackonSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
    },
    validationSchema: trackonInformationSchema,
    onSubmit(values) {
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
        setLoading,
      });
    },
  });

  return (
    <>
      <Loader overly loading={loading}></Loader>
      {showTrackonDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Trackon Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label="Trackon Account"
                    required
                    placeholder="Select"
                    name="accountNo"
                    value={
                      Array.isArray(values?.accountNo)
                        ? values.accountNo.map((item: { value: any }) => item.value)
                        : values.accountNo
                        ? [values.accountNo]
                        : []
                    }
                    options={userTrackonSettings?.accountNo ?? []}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={errors.accountNo as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="DocType"
                    label="Doc Type"
                    required
                    placeholder="Select"
                    name="DocType"
                    value={values.DocType ? [values.DocType] : []}
                    options={userTrackonSettings?.DocType?.length ? userTrackonSettings.DocType : []}
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('DocType', selected);
                    }}
                    error={errors.DocType as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="TypeOfService"
                    label="Type of Service"
                    required
                    placeholder="Select"
                    name="TypeOfService"
                    value={values.TypeOfService ? [values.TypeOfService] : []}
                    options={userTrackonSettings?.TypeOfService?.length ? userTrackonSettings.TypeOfService : []}
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('TypeOfService', selected);
                    }}
                    error={errors.TypeOfService as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="ServiceType"
                    label="Service Type"
                    required
                    placeholder="Select"
                    name="ServiceType"
                    value={values.ServiceType ? [values.ServiceType] : []}
                    options={userTrackonSettings?.ServiceType?.length ? userTrackonSettings.ServiceType : []}
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('ServiceType', selected);
                    }}
                    error={errors.ServiceType as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SbTextField
                    disabled={!isEditable}
                    label={`Pickup Cust Code`}
                    placeholder="Enter Pickup Cust Code"
                    onChange={e => setFieldValue('PickupCustCode', e.target.value)}
                    name="PickupCustCode"
                    value={values.PickupCustCode || ''}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {trackonDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserTrackon;
